import { useState, useEffect, useRef, useCallback, memo } from "react";
import { useAuth, API } from "../App";
import { useBookingsStore } from "../store";
import axios from "axios";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import { EmptyBookings } from "../components/EmptyStates";
import { format, parseISO } from "date-fns";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Hotel,
  Car,
  MapPin,
  CalendarDays,
  Loader2,
  Plus,
  FileText,
  Trash2,
  Home,
  Pencil,
  X,
  ExternalLink,
  Tent,
  Ticket,
  Camera,
  Wallet,
  ScanLine,
  Compass,
} from "lucide-react";
import BarcodeScanner from "../components/BarcodeScanner";
import DriveFilePicker from "../components/DriveFilePicker";
import { EmailImportButton } from "../components/EmailImport";

// Moved BookingForm outside the component to prevent re-renders
const BookingForm = memo(({ 
  booking, 
  onChange, 
  onSubmit, 
  isEdit = false, 
  fileRef,
  cameraInputRef,
  handleFileUpload,
  uploading,
  saving,
  onScanClick,
  onDriveClick,
  trips = []
}) => (
  <div className="space-y-4 mt-4">
    {/* Quick Actions: Scan, Photo & Drive */}
    <div className="flex gap-2">
      <input
        type="file"
        ref={cameraInputRef}
        className="hidden"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleFileUpload(e, isEdit)}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="flex-1 glass-input justify-center border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs sm:text-sm h-9"
        onClick={onScanClick}
        data-testid="scan-barcode-btn"
      >
        <ScanLine className="w-3.5 h-3.5 mr-1.5" />
        <span className="text-xs">Scan</span>
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="flex-1 glass-input justify-center border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-xs sm:text-sm h-9"
        onClick={() => cameraInputRef?.current?.click()}
        disabled={uploading}
        data-testid="camera-btn"
      >
        <Camera className="w-3.5 h-3.5 mr-1.5" />
        <span className="text-xs">Photo</span>
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="flex-1 glass-input justify-center border-sky-500/30 text-sky-400 hover:bg-sky-500/10 text-xs sm:text-sm h-9"
        onClick={onDriveClick}
        data-testid="drive-import-btn"
        title="Import from Google Drive"
      >
        <svg className="w-3.5 h-3.5 mr-1.5" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
          <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
          <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47"/>
          <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/>
          <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
          <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
          <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
        </svg>
        <span className="text-xs">Drive</span>
      </Button>
    </div>

    <div>
      <Label className="text-zinc-400">Type</Label>
      <Select
        value={booking.type}
        onValueChange={(value) => onChange("type", value)}
      >
        <SelectTrigger className="glass-input">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="glass-card border-white/10">
          <SelectItem value="hotel">Hotel</SelectItem>
          <SelectItem value="apartment">Apartment/Airbnb</SelectItem>
          <SelectItem value="car">Car Rental</SelectItem>
          <SelectItem value="activity">Activity/Tour</SelectItem>
          <SelectItem value="camping">Camping/Hostel</SelectItem>
        </SelectContent>
      </Select>
    </div>

    {/* Trip Selector */}
    {trips.length > 0 && (
      <div>
        <Label className="text-zinc-400">Link to Trip (Optional)</Label>
        <Select
          value={booking.trip_id || "none"}
          onValueChange={(value) => onChange("trip_id", value === "none" ? null : value)}
        >
          <SelectTrigger className="glass-input">
            <SelectValue placeholder="Select a trip..." />
          </SelectTrigger>
          <SelectContent className="glass-card border-white/10">
            <SelectItem value="none">No trip</SelectItem>
            {trips.map((trip) => (
              <SelectItem key={trip.trip_id} value={trip.trip_id}>
                {trip.name} ({trip.destination_country})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )}

    <div>
      <Label className="text-zinc-400">Name *</Label>
      <Input
        className="glass-input"
        placeholder="e.g., Hilton Paris, Hertz Rental"
        value={booking.name}
        onChange={(e) => onChange("name", e.target.value)}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        data-testid="booking-name-input"
      />
    </div>

    <div>
      <Label className="text-zinc-400">Provider/Platform</Label>
      <Input
        className="glass-input"
        placeholder="e.g., Booking.com, Airbnb, Enterprise"
        value={booking.provider}
        onChange={(e) => onChange("provider", e.target.value)}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
      />
    </div>

    <div>
      <Label className="text-zinc-400">Location *</Label>
      <Input
        className="glass-input"
        placeholder="City or address"
        value={booking.location}
        onChange={(e) => onChange("location", e.target.value)}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        data-testid="booking-location-input"
      />
    </div>

    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-zinc-400">Check-in / Pickup</Label>
        <Input
          type="date"
          className="glass-input"
          value={booking.check_in}
          onChange={(e) => onChange("check_in", e.target.value)}
        />
      </div>
      <div>
        <Label className="text-zinc-400">Check-out / Return</Label>
        <Input
          type="date"
          className="glass-input"
          value={booking.check_out}
          onChange={(e) => onChange("check_out", e.target.value)}
        />
      </div>
    </div>

    <div>
      <Label className="text-zinc-400">Booking Reference</Label>
      <Input
        className="glass-input"
        placeholder="Confirmation number"
        value={booking.booking_reference}
        onChange={(e) => onChange("booking_reference", e.target.value)}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
      />
    </div>

    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-zinc-400">Price</Label>
        <Input
          type="number"
          step="0.01"
          className="glass-input"
          placeholder=""
          value={booking.price}
          onChange={(e) => onChange("price", e.target.value)}
        />
      </div>
      <div>
        <Label className="text-zinc-400">Currency</Label>
        <Select
          value={booking.currency}
          onValueChange={(value) => onChange("currency", value)}
        >
          <SelectTrigger className="glass-input">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="glass-card border-white/10">
            <SelectItem value="EUR">EUR</SelectItem>
            <SelectItem value="USD">USD</SelectItem>
            <SelectItem value="GBP">GBP</SelectItem>
            <SelectItem value="CHF">CHF</SelectItem>
            <SelectItem value="JPY">JPY</SelectItem>
            <SelectItem value="AUD">AUD</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>

    {/* Budget Integration */}
    {booking.price && (
      <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet className="w-4 h-4 text-emerald-400" />
            <span className="text-white text-sm">Add to My Budget</span>
          </div>
          <Switch
            checked={booking.add_to_budget}
            onCheckedChange={(checked) => onChange("add_to_budget", checked)}
          />
        </div>
        {booking.add_to_budget && (
          <Select
            value={booking.budget_category}
            onValueChange={(value) => onChange("budget_category", value)}
          >
            <SelectTrigger className="glass-input">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent className="glass-card border-white/10">
              <SelectItem value="hotels">Hotels</SelectItem>
              <SelectItem value="car_rental">Car Rental</SelectItem>
              <SelectItem value="sightseeings">Sightseeings</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
    )}

    {/* Attached File Display */}
    {booking.file_name && (
      <div>
        <Label className="text-zinc-400">Attached File</Label>
        <div className="mt-2 p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center gap-2">
          <FileText className="w-4 h-4 text-emerald-400" />
          <a 
            href={booking.file_url?.startsWith('http') ? booking.file_url : `${process.env.REACT_APP_BACKEND_URL}${booking.file_url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-emerald-300 truncate flex-1 hover:text-emerald-200 hover:underline cursor-pointer"
            title="Click to view/download"
          >
            {booking.file_name}
          </a>
          <button
            type="button"
            onClick={() => { onChange("file_url", ""); onChange("file_name", ""); }}
            className="p-1 hover:bg-red-500/20 rounded"
          >
            <X className="w-3 h-3 text-red-400" />
          </button>
        </div>
      </div>
    )}
    
    {/* Hidden file input (used by top buttons) */}
    <input
      type="file"
      ref={fileRef}
      className="hidden"
      accept="image/*,.pdf"
      onChange={(e) => handleFileUpload(e, isEdit)}
    />

    <div>
      <Label className="text-zinc-400">Notes</Label>
      <Input
        className="glass-input"
        placeholder="Additional information"
        value={booking.notes}
        onChange={(e) => onChange("notes", e.target.value)}
        autoComplete="off"
      />
    </div>

    <Button
      className="w-full btn-primary"
      onClick={onSubmit}
      disabled={saving}
      data-testid={isEdit ? "update-booking-btn" : "save-booking-btn"}
    >
      {saving ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
      ) : null}
      {isEdit ? "Update Booking" : "Save Booking"}
    </Button>
  </div>
));

BookingForm.displayName = "BookingForm";

const MyBookings = () => {
  const { user } = useAuth();
  
  // Use Zustand store for bookings
  const { 
    bookings, 
    setBookings, 
    addBooking: storeAddBooking,
    updateBooking: storeUpdateBooking,
    removeBooking: storeRemoveBooking,
  } = useBookingsStore();
  
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showDrivePicker, setShowDrivePicker] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const editCameraInputRef = useRef(null);
  
  const emptyBooking = {
    type: "hotel",
    name: "",
    provider: "",
    location: "",
    check_in: "",
    check_out: "",
    booking_reference: "",
    price: "",
    currency: "EUR",
    notes: "",
    file_url: "",
    file_name: "",
    add_to_budget: false,
    budget_category: "hotels",
    trip_id: null,
  };

  const [newBooking, setNewBooking] = useState(emptyBooking);
  const [editBooking, setEditBooking] = useState(emptyBooking);

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await axios.get(`${API}/bookings`, { withCredentials: true });
        setBookings(response.data);
      } catch (error) {
        setBookings([]);
      } finally {
        setLoading(false);
      }
      try {
        const response = await axios.get(`${API}/trips`, { withCredentials: true });
        setTrips(response.data);
      } catch (error) {
        /* non-critical */
      }
    };
    loadData();
  }, []);

  const handleFileUpload = useCallback(async (event, isEdit = false) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload an image (JPG, PNG, WebP) or PDF file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadResponse = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const fileUrl = uploadResponse.data.url;
      if (isEdit) {
        setEditBooking(prev => ({ ...prev, file_url: fileUrl, file_name: file.name }));
      } else {
        setNewBooking(prev => ({ ...prev, file_url: fileUrl, file_name: file.name }));
      }
      toast.success("File attached");
    } catch (error) {
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
    }
  }, []);

  const addToBudget = async (bookingData, bookingId) => {
    if (!bookingData.add_to_budget || !bookingData.price) return;
    
    try {
      await axios.post(`${API}/budget/expenses`, {
        category: bookingData.budget_category || "hotels",
        description: `${bookingData.type}: ${bookingData.name}`,
        amount: parseFloat(bookingData.price),
        currency: bookingData.currency,
        date: bookingData.check_in || new Date().toISOString().split("T")[0],
        source_id: bookingId,
        source_type: "booking",
      }, {
        withCredentials: true,
      });
      toast.success("Added to budget!");
    } catch (error) {
    }
  };

  const handleAddBooking = async () => {
    if (!newBooking.name || !newBooking.location) {
      toast.error("Please fill in required fields");
      return;
    }

    setSaving(true);
    try {
      const bookingData = {
        ...newBooking,
        price: newBooking.price ? parseFloat(newBooking.price) : null,
      };
      
      const response = await axios.post(`${API}/bookings`, bookingData, {
        withCredentials: true,
      });
      
      // Add to budget if requested (pass the booking_id from the response)
      if (newBooking.add_to_budget && newBooking.price) {
        await addToBudget(newBooking, response.data.booking_id);
      }
      
      setBookings([response.data, ...bookings]);
      toast.success("Booking added successfully");
      setShowAddDialog(false);
      setNewBooking(emptyBooking);
    } catch (error) {
      toast.error("Failed to add booking");
    } finally {
      setSaving(false);
    }
  };

  const handleEditBooking = async () => {
    if (!editBooking.name || !editBooking.location) {
      toast.error("Please fill in required fields");
      return;
    }

    setSaving(true);
    try {
      const bookingData = {
        ...editBooking,
        price: editBooking.price ? parseFloat(editBooking.price) : null,
      };
      
      const response = await axios.put(`${API}/bookings/${selectedBooking.booking_id}`, bookingData, {
        withCredentials: true,
      });
      setBookings(bookings.map(b => b.booking_id === selectedBooking.booking_id ? response.data : b));
      toast.success("Booking updated successfully");
      setShowEditDialog(false);
      setSelectedBooking(null);
    } catch (error) {
      toast.error("Failed to update booking");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to delete this booking?")) return;
    
    try {
      await axios.delete(`${API}/bookings/${bookingId}`, {
        withCredentials: true,
      });
      setBookings(bookings.filter((b) => b.booking_id !== bookingId));
      toast.success("Booking deleted");
    } catch (error) {
      toast.error("Failed to delete booking");
    }
  };

  const openEditDialog = (booking) => {
    setSelectedBooking(booking);
    setEditBooking({
      type: booking.type || "hotel",
      name: booking.name || "",
      provider: booking.provider || "",
      location: booking.location || "",
      check_in: booking.check_in || "",
      check_out: booking.check_out || "",
      booking_reference: booking.booking_reference || "",
      price: booking.price?.toString() || "",
      currency: booking.currency || "EUR",
      notes: booking.notes || "",
      file_url: booking.file_url || "",
      file_name: booking.file_name || "",
      add_to_budget: false,
      budget_category: "hotels",
      trip_id: booking.trip_id || null,
    });
    setShowEditDialog(true);
  };

  const openViewDialog = (booking) => {
    setSelectedBooking(booking);
    setShowViewDialog(true);
  };

  const getTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "hotel": return <Hotel className="w-5 h-5" />;
      case "car": return <Car className="w-5 h-5" />;
      case "apartment": return <Home className="w-5 h-5" />;
      case "activity": return <Ticket className="w-5 h-5" />;
      case "camping": return <Tent className="w-5 h-5" />;
      default: return <MapPin className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "hotel": return "text-rose-400 bg-rose-500/20";
      case "car": return "text-indigo-400 bg-indigo-500/20";
      case "apartment": return "text-amber-400 bg-amber-500/20";
      case "activity": return "text-cyan-400 bg-cyan-500/20";
      case "camping": return "text-green-400 bg-green-500/20";
      default: return "text-emerald-400 bg-emerald-500/20";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Not set";
    try {
      return format(parseISO(dateStr), "MMM d, yyyy");
    } catch {
      return dateStr;
    }
  };

  // Use useCallback to prevent re-creating handlers on every render
  const handleNewBookingChange = useCallback((field, value) => {
    setNewBooking(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleEditBookingChange = useCallback((field, value) => {
    setEditBooking(prev => ({ ...prev, [field]: value }));
  }, []);

  // Handle barcode scan result
  const handleBarcodeScan = useCallback((scanData) => {
    if (scanData.type === "booking_reference" && scanData.parsed?.booking_reference) {
      setNewBooking(prev => ({
        ...prev,
        booking_reference: scanData.parsed.booking_reference,
      }));
      toast.success("Booking reference scanned!");
    } else if (scanData.type === "url") {
      setNewBooking(prev => ({
        ...prev,
        notes: `Scanned URL: ${scanData.parsed.url}`,
      }));
      toast.success("URL scanned and added to notes.");
    } else {
      // Generic barcode - add to booking reference or notes
      const text = scanData.raw;
      if (text.length <= 20 && /^[A-Z0-9]+$/i.test(text)) {
        setNewBooking(prev => ({
          ...prev,
          booking_reference: text,
        }));
        toast.success("Confirmation code scanned!");
      } else {
        setNewBooking(prev => ({
          ...prev,
          notes: prev.notes ? `${prev.notes}\nBarcode: ${text}` : `Barcode: ${text}`,
        }));
        toast.success("Barcode scanned and added to notes.");
      }
    }
  }, []);

  const openScanner = useCallback(() => {
    setShowScanner(true);
  }, []);

  const openDrivePicker = useCallback(() => {
    setShowDrivePicker(true);
  }, []);

  const handleDriveFileSelect = useCallback(async ({ url, fileName }) => {
    // File already uploaded by DriveFilePicker, just update the booking state
    setNewBooking(prev => ({
      ...prev,
      file_url: url,
      file_name: fileName,
    }));
  }, []);

  const importBookingFromEmail = useCallback(async (booking) => {
    const bookingData = {
      type: booking.type || "hotel",
      provider: booking.provider || null,
      booking_reference: booking.booking_reference || null,
      name: booking.name || booking.provider || "Imported Booking",
      check_in: booking.check_in || null,
      check_out: booking.check_out || null,
      location: booking.location || null,
      price: booking.price != null ? parseFloat(booking.price) : null,
      currency: booking.currency || "EUR",
      notes: booking.notes || null,
    };
    const response = await axios.post(`${API}/bookings`, bookingData, { withCredentials: true });
    storeAddBooking(response.data);
  }, []);

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="page-container p-4 pt-6 pb-28" data-testid="my-bookings">
      <SEO title="My Bookings" description="Manage hotel, car rental, and accommodation bookings. Store confirmations and details in one place." path="/bookings" />
      <div className="max-w-4xl mx-auto">
      {/* Hero Banner */}
      <div className="relative h-36 sm:h-44 lg:h-52 overflow-hidden -mx-4 -mt-6 mb-6 rounded-b-2xl">
        <img src="https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80" alt="" className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080d19]/80 via-[#080d19]/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4 pb-5 flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-bold font-heading text-white drop-shadow-lg">My Bookings</h1>
            <p className="text-sm text-slate-300/80 mt-0.5 drop-shadow">Hotels, rentals & stays</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="btn-primary text-sm" data-testid="add-booking-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Booking
                </Button>
              </DialogTrigger>
          <DialogContent className="glass-card border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Add New Booking</DialogTitle>
            </DialogHeader>
            <BookingForm
              booking={newBooking}
              onChange={handleNewBookingChange}
              onSubmit={handleAddBooking}
              fileRef={fileInputRef}
              cameraInputRef={cameraInputRef}
              handleFileUpload={handleFileUpload}
              uploading={uploading}
              saving={saving}
              onScanClick={openScanner}
              onDriveClick={openDrivePicker}
              trips={trips}
            />
          </DialogContent>
        </Dialog>
          <EmailImportButton
            mode="booking"
            onImportBookings={importBookingFromEmail}
          />
          </div>
        </div>
      </div>

      {/* Barcode Scanner */}
      <BarcodeScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleBarcodeScan}
        title="Scan Booking Confirmation"
      />

      {/* Google Drive File Picker */}
      <DriveFilePicker
        isOpen={showDrivePicker}
        onClose={() => setShowDrivePicker(false)}
        onFileSelect={handleDriveFileSelect}
        acceptedTypes={['application/pdf', 'image/']}
        title="Import Booking from Google Drive"
      />

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="glass-card border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Booking</DialogTitle>
          </DialogHeader>
          <BookingForm
            booking={editBooking}
            onChange={handleEditBookingChange}
            onSubmit={handleEditBooking}
            isEdit={true}
            fileRef={editFileInputRef}
            cameraInputRef={editCameraInputRef}
            handleFileUpload={handleFileUpload}
            uploading={uploading}
            saving={saving}
            onScanClick={openScanner}
            onDriveClick={openDrivePicker}
            trips={trips}
          />
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="glass-card border-white/10 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {selectedBooking && getTypeIcon(selectedBooking.type)}
              Booking Details
            </DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-zinc-500 text-xs">Name</Label>
                <p className="text-white text-lg font-semibold">{selectedBooking.name}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-500 text-xs">Type</Label>
                  <p className="text-white capitalize">{selectedBooking.type}</p>
                </div>
                {selectedBooking.provider && (
                  <div>
                    <Label className="text-zinc-500 text-xs">Provider</Label>
                    <p className="text-white">{selectedBooking.provider}</p>
                  </div>
                )}
              </div>
              
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-2 text-white">
                  <MapPin className="w-4 h-4 text-emerald-400" />
                  {selectedBooking.location}
                </div>
              </div>

              {(selectedBooking.check_in || selectedBooking.check_out) && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-500 text-xs">Check-in / Pickup</Label>
                    <p className="text-white">{formatDate(selectedBooking.check_in)}</p>
                  </div>
                  <div>
                    <Label className="text-zinc-500 text-xs">Check-out / Return</Label>
                    <p className="text-white">{formatDate(selectedBooking.check_out)}</p>
                  </div>
                </div>
              )}

              {selectedBooking.booking_reference && (
                <div>
                  <Label className="text-zinc-500 text-xs">Booking Reference</Label>
                  <p className="text-white font-mono">{selectedBooking.booking_reference}</p>
                </div>
              )}

              {selectedBooking.price && (
                <div>
                  <Label className="text-zinc-500 text-xs">Price</Label>
                  <p className="text-sky-400 font-medium text-lg">
                    {selectedBooking.currency} {selectedBooking.price}
                  </p>
                </div>
              )}

              {selectedBooking.notes && (
                <div>
                  <Label className="text-zinc-500 text-xs">Notes</Label>
                  <p className="text-zinc-300">{selectedBooking.notes}</p>
                </div>
              )}

              {selectedBooking.file_name && (
                <div>
                  <Label className="text-zinc-500 text-xs">Attached File</Label>
                  <div className="mt-1 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center justify-between">
                    <a 
                      href={selectedBooking.file_url?.startsWith('http') ? selectedBooking.file_url : `${process.env.REACT_APP_BACKEND_URL}${selectedBooking.file_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 flex-1 hover:opacity-80"
                    >
                      <FileText className="w-5 h-5 text-emerald-400" />
                      <span className="text-emerald-300 hover:underline">{selectedBooking.file_name}</span>
                    </a>
                    <a
                      href={selectedBooking.file_url?.startsWith('http') ? selectedBooking.file_url : `${process.env.REACT_APP_BACKEND_URL}${selectedBooking.file_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-emerald-500/20 rounded-lg transition-colors"
                      title="Open file"
                    >
                      <ExternalLink className="w-4 h-4 text-emerald-400" />
                    </a>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 btn-outline"
                  onClick={() => {
                    setShowViewDialog(false);
                    openEditDialog(selectedBooking);
                  }}
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 btn-outline text-red-400 border-red-500/30 hover:bg-red-500/10"
                  onClick={() => {
                    setShowViewDialog(false);
                    handleDeleteBooking(selectedBooking.booking_id);
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {bookings.length === 0 ? (
        <EmptyBookings onAction={() => setShowAddDialog(true)} />
      ) : (
        <div className="space-y-4">
          {bookings.map((booking) => (
            <div
              key={booking.booking_id}
              className="glass-card p-4 hover:border-sky-500/30 transition-all cursor-pointer"
              onClick={() => openViewDialog(booking)}
              data-testid={`booking-card-${booking.booking_id}`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl ${getTypeColor(booking.type)}`}>
                  {getTypeIcon(booking.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-white">{booking.name}</h3>
                    <span className="text-xs uppercase tracking-wider text-zinc-500">
                      {booking.type}
                    </span>
                  </div>
                  {booking.provider && (
                    <p className="text-sm text-zinc-400 mt-1">{booking.provider}</p>
                  )}
                  {booking.location && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-zinc-500">
                      <MapPin className="w-4 h-4" />
                      <span>{booking.location}</span>
                    </div>
                  )}
                  {booking.check_in && booking.check_out && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-zinc-500">
                      <CalendarDays className="w-4 h-4" />
                      <span>
                        {formatDate(booking.check_in)} - {formatDate(booking.check_out)}
                      </span>
                    </div>
                  )}
                  <div className="mt-3 flex items-center justify-between">
                    {booking.booking_reference && (
                      <span className="text-xs text-zinc-500">
                        Ref: {booking.booking_reference}
                      </span>
                    )}
                    {booking.price && (
                      <span className="text-sky-400 font-medium">
                        {booking.currency} {booking.price}
                      </span>
                    )}
                  </div>
                  {booking.trip_id && trips.find(t => t.trip_id === booking.trip_id) && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-violet-400">
                      <Compass className="w-3 h-3" />
                      <span>{trips.find(t => t.trip_id === booking.trip_id)?.name}</span>
                    </div>
                  )}
                  {booking.file_name && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-emerald-400">
                      <FileText className="w-3 h-3" />
                      <span>{booking.file_name}</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditDialog(booking);
                    }}
                    className="p-2 hover:bg-sky-500/20 rounded transition-colors"
                    data-testid={`edit-booking-${booking.booking_id}`}
                  >
                    <Pencil className="w-4 h-4 text-sky-400" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteBooking(booking.booking_id);
                    }}
                    className="p-2 hover:bg-red-500/20 rounded transition-colors"
                    data-testid={`delete-booking-${booking.booking_id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
};

export default MyBookings;
